/*
XOL-67 (RTA redesign v2.1, V2): the pure parts of the REQUEST form, shared by the create form (AcquireRequest) and, in V3, the Step 1 form
(RtaRequest). Both show the same fields in `RtaRequestFields`, which holds the request as ONE flat object keyed by the wire names of
`state.nominal` (lib/rtaNominal.NOMINAL_FIELDS), so a value never changes shape between the form, the diff (nominalChanges) and the body.

  REQUEST_FIELDS            the fields the form shows: the point, the locality, the requirements (not Site Name / Site ID)
  CREATE_FIELDS             REQUEST_FIELDS plus Site Name and Site ID: what the create form checks at Save (v2.2, decision 168)
  FIELD_ORDER               the order the create form shows them in (decision 183: Details, then Structure Details): the first one in error takes focus
  emptyRequestValues()      every request field null (Site Name / Site ID too)
  localityPath(values)      [region, state, district, subdistrict, zone], positional (an empty level stays empty): what mapCentre and the name read
  localityKeys(path)        the five locality keys of a cascader path, an unset level as null
  localityShort(path, regions)  does a stored locality path stop ABOVE a leaf of the locality tree (or leave it)? An Element cascader shows only a path that ends at a leaf,
                            so it would show nothing for it
  requestProblems(values, fields)   { field: message } for the fields given (default: all of them), in the SAME sentences the server's 422 gets
                            (rtaNominal.nominalFieldProblem / pointProblem), the point's pair rule included on BOTH coordinates

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

import { coordinate, nominalFieldProblem, pointProblem } from '@/lib/rtaNominal'

export var REQUEST_FIELDS = [
  'latitude', 'longitude', 'region', 'state', 'district', 'subdistrict', 'zone',
  'stationType', 'structure_id', 'structureHeight', 'antennaHeight', 'width', 'depth'
]

// What the create form checks at Save: both names (required of every creator, decision 168) and the request.
export var CREATE_FIELDS = ['siteName', 'siteId'].concat(REQUEST_FIELDS)

// The create form's order, top to bottom (v2.2 decision 176, Carlos): the LEFT column (the client, the two names, Station Type, the locality), the RIGHT column (the
// five structure fields), then the point (Lat, Lng; the place search beside them; the map) last. The locality's error is keyed `state`.
export var FIELD_ORDER = [
  'client', 'siteName', 'siteId', 'state', 'latitude', 'longitude', 'stationType', 'structure_id', 'structureHeight', 'antennaHeight', 'width', 'depth'
]

var LOCALITY_KEYS = ['region', 'state', 'district', 'subdistrict', 'zone']

/** A request with nothing in it: every field of the form, Site Name and Site ID included, null. */
export function emptyRequestValues () {
  var out = { siteName: null, siteId: null }

  REQUEST_FIELDS.forEach(function (k) { out[k] = null })

  return out
}

function present (v) {
  return v !== undefined && v !== null && String(v).trim() !== ''
}

/** The five locality levels of the values, positional (region, state, district, subdistrict, zone): an empty level stays an empty slot. */
export function localityPath (values) {
  var v = values || {}

  return LOCALITY_KEYS.map(function (k) { return present(v[k]) ? String(v[k]).trim() : null })
}

/** The cascader's path as the five locality keys: { region, state, district, subdistrict, zone }, a level it did not reach as null. */
export function localityKeys (path) {
  var p = Array.isArray(path) ? path : []
  var out = {}

  LOCALITY_KEYS.forEach(function (k, i) { out[k] = present(p[i]) ? p[i] : null })

  return out
}

/**
 * The problems of `fields` (default every REQUEST_FIELD) in the values, as { field: message }: each field by nominalFieldProblem (the locality is optional;
 * the heights 1 to 121; width and depth whole metres; the coordinates in range; with `siteName` / `siteId` in `fields`, both required) and, when a coordinate is in `fields`, the pair rule
 * (pointProblem: both or neither) on BOTH coordinates, as the server's 422 on the pair does. A 0 coordinate is "unset", as everywhere.
 */
export function requestProblems (values, fields) {
  var v = values || {}
  var list = Array.isArray(fields) ? fields : REQUEST_FIELDS
  var out = {}

  list.forEach(function (f) {
    var message = nominalFieldProblem(f, f === 'latitude' || f === 'longitude' ? coordinate(v[f]) : v[f])

    if (message) {
      out[f] = message
    }
  })

  if (list.indexOf('latitude') !== -1 || list.indexOf('longitude') !== -1) {
    var pair = pointProblem(coordinate(v.latitude), coordinate(v.longitude))

    if (pair) {
      out.latitude = pair
      out.longitude = pair
    }
  }

  return out
}

/**
 * Does this locality path stop above a leaf of the tree (`regions`: the tree of malaysia.json, { name, divisions: [...] }), or leave it? An Element
 * cascader shows only a path that ENDS AT A LEAF, so for a stored region / state / district with no mukim it shows nothing, which reads as "no locality"
 * on a required field. The create form always stores a leaf; an RTA made some other way can end higher. False for an empty path (nothing is stored).
 */
export function localityShort (path, regions) {
  var levels = (Array.isArray(path) ? path : []).filter(function (l) { return present(l) })

  if (levels.length === 0) {
    return false
  }

  var list = Array.isArray(regions) ? regions : []
  var node = null

  for (var i = 0; i < levels.length; i++) {
    node = null

    for (var j = 0; j < list.length; j++) {
      if (list[j] && list[j].name === levels[i]) {
        node = list[j]
        break
      }
    }

    if (node === null) {
      return true
    }

    list = Array.isArray(node.divisions) ? node.divisions : []
  }

  return list.length > 0
}
